import type { GameManifest } from '../types.ts'

/** Kolik příček žebříčku ukázat v náhledu. */
const LEVEL_PREVIEW_COUNT = 8

export const manifest: GameManifest = {
  id: 'mot-spheres',
  name: 'Sledování objektů (3D-MOT)',
  tagline: 'Udrž v hlavě několik pohybujících se cílů mezi rušiči.',
  description:
    'Klasické cvičení Multiple Object Tracking. V průhledné krychli se objeví sada objektů, ' +
    'část z nich se na chvíli rozsvítí jako cíle. Pak všechny zhasnou, začnou se pohybovat ' +
    'a odrážet od stěn. Po zastavení je úkolem označit původní cíle. Trénuje se dělená ' +
    'a udržovaná pozornost, periferní vidění a pracovní paměť. Rychlost se po každém pokusu ' +
    'automaticky přizpůsobuje výkonu, takže cvičení drží klienta trvale na hraně jeho možností. ' +
    'Výsledkem série je prahová rychlost — číslo, které se sleduje v čase.',
  version: '1.0.0',
  category: 'tracking',
  icon: '🔵',

  params: [
    {
      key: 'ballCount',
      label: 'Počet objektů',
      type: 'int',
      min: 4,
      max: 16,
      step: 1,
      default: 8,
      group: 'Obtížnost',
      help: 'Celkový počet objektů ve scéně. Standard je 8.',
    },
    {
      key: 'targetCount',
      label: 'Počet cílů',
      type: 'int',
      min: 1,
      max: 8,
      step: 1,
      default: 4,
      group: 'Obtížnost',
      help: 'Kolik objektů se má sledovat. Začátečník 1–2, pokročilý 4, expert 5+. ' +
        'Musí být menší než počet objektů.',
    },
    {
      key: 'startSpeed',
      label: 'Počáteční rychlost',
      type: 'float',
      min: 0.05,
      max: 4,
      step: 0.05,
      default: 0.55,
      unit: '×',
      group: 'Obtížnost',
      help: 'Startovní rychlost série. Ideálně nastav zhruba na prahovou rychlost ' +
        'z minulého tréninku — série se pak rychleji „chytne". Hodnota 1,0 znamená, ' +
        'že objekt přeletí krychli za dvě sekundy.',
    },
    {
      key: 'trackSeconds',
      label: 'Doba sledování',
      type: 'float',
      min: 3,
      max: 20,
      step: 0.5,
      default: 8,
      unit: 's',
      group: 'Průběh',
      help: 'Jak dlouho se objekty pohybují, než se zastaví. Standard je 8 sekund.',
    },
    {
      key: 'trials',
      label: 'Počet pokusů v sérii',
      type: 'int',
      min: 5,
      max: 40,
      step: 1,
      default: 20,
      group: 'Průběh',
      help: 'Délka jedné tréninkové série. Standard je 20 pokusů (zhruba 6–8 minut).',
    },
    {
      key: 'presentSeconds',
      label: 'Doba zvýraznění cílů',
      type: 'float',
      min: 1,
      max: 6,
      step: 0.5,
      default: 2,
      unit: 's',
      group: 'Průběh',
      advanced: true,
      help: 'Jak dlouho cíle na začátku pokusu svítí, než zhasnou a rozjedou se.',
    },
    {
      key: 'speedUp',
      label: 'Zrychlení po úspěchu',
      type: 'float',
      min: 1.01,
      max: 1.8,
      step: 0.01,
      default: 1.28,
      unit: '×',
      group: 'Adaptace',
      help: 'Násobič rychlosti po zcela správném pokusu. Při 1,28 je každý další ' +
        'pokus o 28 % rychlejší — rozdíl je jasně znát. Sniž, pokud klient ' +
        'přeskakuje svou úroveň příliš rychle.',
    },
    {
      key: 'speedDown',
      label: 'Zpomalení po chybě',
      type: 'float',
      min: 0.4,
      max: 0.99,
      step: 0.01,
      default: 0.8,
      unit: '×',
      group: 'Adaptace',
      help: 'Násobič rychlosti po chybném pokusu.',
    },
    {
      key: 'adaptiveStep',
      label: 'Zjemňovat krok během série',
      type: 'bool',
      default: true,
      group: 'Adaptace',
      help: 'Zjemňuje postupně pokles po chybách, aby se série kolem hranice klienta ' +
        'jemně doladila místo skákání sem a tam. Postupu nahoru se to netýká — ' +
        'úspěšný pokus vždy posune na další level.',
    },
    {
      key: 'depth',
      label: 'Pohyb do hloubky',
      type: 'bool',
      default: true,
      group: 'Scéna',
      help: 'Objekty se pohybují i směrem k divákovi a od něj. Zapojuje vergenci očí — ' +
        'náročnější a blíž reálné hře. Vypni u klientů s obtížemi v prostorovém vidění.',
    },
    {
      key: 'fixation',
      label: 'Fixační bod uprostřed',
      type: 'bool',
      default: true,
      group: 'Scéna',
      help: 'Zobrazí terčík ve středu. Klient se dívá na něj a objekty sleduje periferií — ' +
        'to je smyslem cvičení. Bez fixace se cvičí spíš rychlost očních pohybů.',
    },
    {
      key: 'ballSize',
      label: 'Velikost objektů',
      type: 'float',
      min: 0.5,
      max: 1.6,
      step: 0.05,
      default: 1,
      unit: '×',
      group: 'Scéna',
      advanced: true,
      help: 'Menší objekty zvyšují nároky na zrakovou ostrost v periferii.',
    },
    {
      key: 'collisions',
      label: 'Odrážení objektů od sebe',
      type: 'bool',
      default: true,
      group: 'Scéna',
      advanced: true,
      help: 'Objekty se navzájem odrážejí místo prolínání. Vytváří překryvy a záměny, ' +
        'které dělají sledování náročnějším.',
    },
    {
      key: 'sound',
      label: 'Zvukové signály',
      type: 'bool',
      default: true,
      group: 'Scéna',
      help: 'Krátké tóny při startu, zastavení a vyhodnocení. Klient se pak nemusí ' +
        'dívat po ovládacích prvcích a udrží pohled ve středu.',
    },
    {
      key: 'feedback',
      label: 'Zpětná vazba po pokusu',
      type: 'enum',
      options: [
        { value: 'full', label: 'Ukázat správné i chybné' },
        { value: 'count', label: 'Jen počet správných' },
        { value: 'none', label: 'Žádná' },
      ],
      default: 'full',
      group: 'Průběh',
      advanced: true,
      help: 'Plná zpětná vazba pomáhá učení, žádná lépe měří skutečný výkon.',
    },
  ],

  // Skill je primární proto, že jako jediný jde srovnávat napříč nastaveními.
  // Prahová rychlost 0,9 se dvěma cíli a s pěti jsou úplně jiné výkony;
  // skill obojí převede na společnou stupnici.
  primaryMetric: {
    key: 'skill',
    label: 'Skill index',
    higherIsBetter: true,
    precision: 1,
  },

  secondaryMetrics: [
    {
      key: 'maxLevel',
      label: 'Nejvyšší zvládnutý level',
      higherIsBetter: true,
      precision: 0,
    },
    {
      key: 'thresholdLevel',
      label: 'Udržitelný level',
      higherIsBetter: true,
      precision: 0,
    },
    {
      key: 'speedThreshold',
      label: 'Prahová rychlost',
      higherIsBetter: true,
      precision: 3,
    },
    {
      key: 'accuracy',
      label: 'Úspěšnost',
      unit: '%',
      higherIsBetter: true,
      precision: 0,
    },
    {
      key: 'maxSpeed',
      label: 'Nejvyšší zvládnutá rychlost',
      higherIsBetter: true,
      precision: 3,
    },
    {
      key: 'responsePerTarget',
      label: 'Rozhodnutí na jeden cíl',
      unit: 's',
      higherIsBetter: false,
      precision: 2,
    },
    {
      key: 'meanResponseMs',
      label: 'Průměrná doba odpovědi',
      unit: 'ms',
      higherIsBetter: false,
      precision: 0,
    },
  ],

  /**
   * Rychlosti tvoří geometrickou řadu: každý level je o `speedUp` rychlejší
   * než předchozí. Mřížka je tak daná hned na začátku série a úspěšný pokus
   * vždy posune přesně na další příčku.
   */
  levels: (config) => {
    const start = typeof config.startSpeed === 'number' ? config.startSpeed : 0.55
    const step = typeof config.speedUp === 'number' ? config.speedUp : 1.28

    return Array.from({ length: LEVEL_PREVIEW_COUNT }, (_, i) => ({
      level: i + 1,
      value: (start * step ** i).toFixed(2),
    }))
  },
}

export default manifest
